import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkGrid from "@/components/feature/WorkGrid";
import Pagination from "@/components/feature/Pagination";
import FilterSidebar from "@/pages/category/components/FilterSidebar";
import { filterWorks, paginate, PAGE_SIZE } from "@/utils/work";
import type { WorkType } from "@/mocks/movies";

const TYPE_META: Record<string, { title: string; desc: string; crumb: string }> = {
  movie: {
    title: "韩国伦理电影",
    desc: "精选韩国伦理题材电影，涵盖伦理情感、家庭伦理、伦理悬疑等多种题材，满足不同观影偏好。",
    crumb: "电影",
  },
  tvseries: {
    title: "韩国伦理电视剧",
    desc: "汇集韩国伦理题材电视剧，从家庭群像到都市悬疑，完整剧集信息一页浏览。",
    crumb: "电视剧",
  },
  all: {
    title: "全部伦理影视作品",
    desc: "浏览全部韩国伦理题材电影与电视剧，支持按题材、年份与热度筛选。",
    crumb: "全部作品",
  },
};

export default function Category() {
  const { type: typeParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<"page" | "more">("page");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE * 2);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const type = (typeParam === "movie" || typeParam === "tvseries" ? typeParam : "all") as
    | WorkType
    | "all";
  const meta = TYPE_META[type] ?? TYPE_META.all;

  const genre = searchParams.get("filter") || "all";
  const year = searchParams.get("year") || "all";
  const sort = searchParams.get("sort") || "popularity";
  const page = Number(searchParams.get("page") || "1");

  const filtered = useMemo(
    () => filterWorks({ type, genre, yearRange: year, sort }),
    [type, genre, year, sort],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pagedWorks = paginate(filtered, page, PAGE_SIZE);
  const moreWorks = filtered.slice(0, visibleCount);

  useEffect(() => {
    setLoading(true);
    setVisibleCount(PAGE_SIZE * 2);
    const timer = window.setTimeout(() => setLoading(false), 420);
    return () => window.clearTimeout(timer);
  }, [type, genre, year, sort, page]);

  const updateParam = (key: string, value: string, defaultValue: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === defaultValue) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    if (key !== "page") next.delete("page");
    setSearchParams(next);
    setMobileFilterOpen(false);
  };

  const handleReset = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("filter");
    next.delete("year");
    next.delete("sort");
    next.delete("page");
    setSearchParams(next);
    setMobileFilterOpen(false);
  };

  const filterPanel = (
    <FilterSidebar
      genre={genre}
      year={year}
      sort={sort}
      onGenreChange={(v) => updateParam("filter", v, "all")}
      onYearChange={(v) => updateParam("year", v, "all")}
      onSortChange={(v) => updateParam("sort", v, "popularity")}
      onReset={handleReset}
    />
  );

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full px-4 py-8 md:px-8">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: type === "all" ? "全部作品" : meta.crumb },
            ]}
          />
          <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {meta.title} - 韩国伦理在线观看
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-500">
            {meta.desc}
          </p>
        </div>
      </div>

      <div className="mx-auto w-full px-4 py-8 md:px-8">
        <div className="flex flex-col gap-6 lg:flex-row">
          <aside className="hidden w-64 shrink-0 lg:block">
            <div className="sticky top-20">{filterPanel}</div>
          </aside>

          <div className="min-w-0 flex-1">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-600">
                共找到 <span className="font-bold text-accent-600">{filtered.length}</span> 部作品
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setMobileFilterOpen((prev) => !prev)}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs font-medium text-foreground-700 lg:hidden"
                >
                  <i className="ri-equalizer-line text-base" />
                  筛选
                </button>
                <div className="flex items-center gap-1 rounded-md border border-background-300 bg-background-50 px-1 py-1">
                  <button
                    type="button"
                    onClick={() => setViewMode("page")}
                    className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      viewMode === "page"
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600"
                    }`}
                  >
                    分页
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setViewMode("more");
                      setVisibleCount(PAGE_SIZE * 2);
                    }}
                    className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      viewMode === "more"
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600"
                    }`}
                  >
                    加载更多
                  </button>
                </div>
              </div>
            </div>

            {mobileFilterOpen && <div className="mb-5 lg:hidden">{filterPanel}</div>}

            {viewMode === "page" ? (
              <>
                <WorkGrid
                  works={pagedWorks}
                  loading={loading}
                  skeletonCount={6}
                  columns={4}
                  emptyTitle="未找到符合条件的作品"
                  emptyDescription="当前筛选条件下没有结果，试试放宽条件或重置筛选。"
                  emptyActionLabel="重置筛选"
                  onEmptyAction={handleReset}
                />
                {!loading && filtered.length > 0 && (
                  <Pagination
                    page={page}
                    totalPages={totalPages}
                    onChange={(p) => updateParam("page", String(p), "1")}
                  />
                )}
              </>
            ) : (
              <>
                <WorkGrid
                  works={moreWorks}
                  loading={loading}
                  skeletonCount={6}
                  columns={4}
                  emptyTitle="未找到符合条件的作品"
                  emptyDescription="当前筛选条件下没有结果，试试放宽条件或重置筛选。"
                  emptyActionLabel="重置筛选"
                  onEmptyAction={handleReset}
                />
                {!loading && visibleCount < filtered.length && (
                  <div className="mt-10 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                      className="flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-primary-600 transition-colors hover:bg-primary-50"
                    >
                      <i className="ri-add-line text-base" />
                      加载更多作品
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}